import '../text-and-lang/as-text.ts';
import '../parameters/as-param-group.ts';
import '../base/buttons/as-button.ts';

/**
 * <as-export-preview-panel>
 *
 * Preview card placed as a sibling before <as-export-step> inside an
 * <as-step-panel>. Uses display:contents (light DOM) so the inner as-param-group
 * card participates directly in the step panel's flex layout.
 *
 * Children placed inside this element in HTML or JS are moved into the card at
 * build time and automatically disabled while preview is inactive, re-enabled when
 * the "Show export preview" checkbox is checked. Children added later via JS are
 * also handled via MutationObserver.
 *
 * Attributes
 *   subheading-default-text   — card heading (default: "Design preview")
 *   subheading-dict-key       — i18n key for the heading
 *   descr-default-text        — override for the description paragraph text
 *   descr-text-dict-key       — i18n key for the description paragraph
 *
 * Events
 *   as-preview-toggle   bubbles, composed — detail: { active: boolean }
 *
 * API
 *   get previewActive: boolean
 */
export class AsExportPreviewPanel extends HTMLElement {
  #previewActive = false;
  #built = false;
  #toggleBtnEl: HTMLElement | null = null;
  #cardEl: HTMLElement | null = null;
  #descTextEl: HTMLElement | null = null;
  #userEls: HTMLElement[] = [];
  #observer: MutationObserver | null = null;
  #onResetBound = (): void => this._handleSessionReset();

  static readonly observedAttributes = [
    'subheading-default-text', 'subheading-dict-key', 'descr-default-text', 'descr-text-dict-key',
  ];

  constructor() {
    super();
  }

  attributeChangedCallback(name: string, old: string | null, next: string | null): void {
    if (old === next) return;
    if (name === 'subheading-default-text' || name === 'subheading-dict-key') this._updateSubheading();
    if (name === 'descr-default-text' || name === 'descr-text-dict-key') this._updateDescription();
  }

  connectedCallback(): void {
    this.style.display = 'contents';
    if (!this.#built) this._build();
    // Reset on a new session. `as-inputs-reset` is the canonical broadcast fired
    // by managed project handling (createAndSetNewProject); `as-session-manual-reset`
    // is the explicit hook for hosts running with disable-session-handlers, which
    // never trigger the managed path.
    window.addEventListener('as-inputs-reset', this.#onResetBound);
    window.addEventListener('as-session-manual-reset', this.#onResetBound);
  }

  disconnectedCallback(): void {
    this.#observer?.disconnect();
    this.#observer = null;
    window.removeEventListener('as-inputs-reset', this.#onResetBound);
    window.removeEventListener('as-session-manual-reset', this.#onResetBound);
  }

  get previewActive(): boolean { return this.#previewActive; }

  // ── Private ───────────────────────────────────────────────────────────────────

  /**
   * Restore the panel to its initial state on a new session: turn the preview
   * toggle off, clear the active flag, and notify listeners so any active preview
   * is torn down. No-ops if preview was already inactive.
   */
  private _handleSessionReset(): void {
    (this.#toggleBtnEl as any)?.forceToggleState?.(false);
    if (!this.#previewActive) return;
    this.#previewActive = false;
    this.dispatchEvent(new CustomEvent('as-preview-toggle', {
      bubbles: true, composed: true,
      detail: { active: false },
    }));
  }

  private _build(): void {
    this.#built = true;

    const group = document.createElement('as-param-group');
    this.#cardEl = group;

    const p = document.createElement('p');
    p.className = 'desc';
    p.style.cssText = 'font-size:0.875rem;color:hsl(var(--muted-foreground));margin:0;';
    const textEl = document.createElement('as-text');
    this._applyDescription(textEl);
    this.#descTextEl = textEl;
    p.appendChild(textEl);
    group.appendChild(p);

    const btn = document.createElement('as-button');
    btn.setAttribute('toggle', '');
    btn.setAttribute('name', 'as-export-preview-toggle');
    btn.setAttribute('default-text', 'Show export preview');
    btn.setAttribute('toggle-on-default-text', 'Hide export preview');
    this.#toggleBtnEl = btn;
    btn.addEventListener('as-button-click', (e: Event) => {
      this.#previewActive = (e as CustomEvent).detail?.toggledOn ?? false;
      this.dispatchEvent(new CustomEvent('as-preview-toggle', {
        bubbles: true, composed: true,
        detail: { active: this.#previewActive },
      }));
    });
    group.appendChild(btn);

    // Adopt any children already in the element (from HTML authoring)
    for (const child of Array.from(this.children) as HTMLElement[]) {
      this.#userEls.push(child);
      group.appendChild(child);
    }

    this.appendChild(group);
    this._updateSubheading();

    // Watch for children added dynamically after build
    this.#observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        for (const node of Array.from(m.addedNodes)) {
          if (node instanceof HTMLElement && node !== group) {
            this.#userEls.push(node);
            group.appendChild(node);
          }
        }
      }
    });
    this.#observer.observe(this, { childList: true });
  }

  private _updateSubheading(): void {
    if (!this.#cardEl) return;
    const dictKey = this.getAttribute('subheading-dict-key');
    const defaultText = this.getAttribute('subheading-default-text') ?? 'Design preview';
    if (dictKey) {
      this.#cardEl.setAttribute('subheading-dict-key', dictKey);
      this.#cardEl.setAttribute('subheading-default-text', defaultText);
    } else {
      this.#cardEl.removeAttribute('subheading-dict-key');
      this.#cardEl.setAttribute('subheading-default-text', defaultText);
    }
  }

  private _updateDescription(): void {
    if (!this.#descTextEl) return;
    this._applyDescription(this.#descTextEl);
  }

  private _applyDescription(el: HTMLElement): void {
    const dictKey = this.getAttribute('descr-text-dict-key');
    const defaultText = this.getAttribute('descr-default-text')
      ?? 'Preview your final, printable part before exporting.';
    if (dictKey) el.setAttribute('dict-key', dictKey);
    else el.removeAttribute('dict-key');
    el.textContent = defaultText;
  }

}

if (!customElements.get('as-export-preview-panel')) {
  customElements.define('as-export-preview-panel', AsExportPreviewPanel);
} else {
  console.warn('as-export-preview-panel already defined');
}
